/**
 * Manage Hub — centralized data management with sidebar + entity list + slide-out panels.
 * Phase 18 + Phase 25b: Added universal Search tab.
 */

import { useState } from 'react';
import { SearchBar } from '../components/common';
import SearchPanel from '../components/manage/SearchPanel';
import CustomerList from '../components/manage/CustomerList';
import ContactList from '../components/manage/ContactList';
import VendorList from '../components/manage/VendorList';
import PartList from '../components/manage/PartList';
import ParentItemList from '../components/manage/ParentItemList';
import BomVersionList from '../components/manage/BomVersionList';
import UserList from '../components/manage/UserList';

const ENTITIES = [
  { key: 'search', label: 'Search', icon: 'search' },
  { key: 'customers', label: 'Customers', icon: 'users' },
  { key: 'contacts', label: 'Contacts', icon: 'contact' },
  { key: 'vendors', label: 'Vendors', icon: 'truck' },
  { key: 'parts', label: 'Parts', icon: 'cube' },
  { key: 'parent-items', label: 'Parent Items', icon: 'layers' },
  { key: 'bom-versions', label: 'BOM Versions', icon: 'list' },
  { key: 'users', label: 'Users', icon: 'user' },
];

const ICONS = {
  search: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  ),
  users: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  ),
  contact: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  ),
  truck: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
  ),
  cube: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
  ),
  layers: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  ),
  list: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  ),
  user: (
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  ),
};

export default function ManagePage() {
  const [activeEntity, setActiveEntity] = useState('search');
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  const isSearch = activeEntity === 'search';

  return (
    <div className="flex flex-col lg:flex-row gap-0 lg:-mx-6 lg:-mt-4" style={{ height: 'calc(100vh - 64px)' }}>
      {/* Entity selector — horizontal on mobile, sidebar on desktop */}
      <div className="lg:w-52 flex-shrink-0 bg-gray-50 border-b lg:border-b-0 lg:border-r border-gray-200 lg:py-3">
        <h2 className="hidden lg:block px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
          Manage
        </h2>
        <nav className="flex lg:flex-col gap-1 px-2 py-2 lg:py-0 lg:space-y-0.5 overflow-x-auto lg:overflow-x-visible">
          {ENTITIES.map((entity, i) => (
            <div key={entity.key}>
              {/* Separator after Search */}
              {i === 1 && <div className="hidden lg:block border-t border-gray-200 my-1" />}
              <button
                onClick={() => { setActiveEntity(entity.key); setSearch(''); }}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap w-full text-left ${
                  activeEntity === entity.key
                    ? entity.key === 'search'
                      ? 'bg-blue-100 text-blue-800 font-semibold'
                      : 'bg-blue-50 text-blue-700 font-medium'
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                }`}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {ICONS[entity.icon]}
                </svg>
                <span className="hidden lg:inline">{entity.label}</span>
                <span className="lg:hidden text-xs">{entity.label}</span>
              </button>
            </div>
          ))}
        </nav>
      </div>

      {/* Right side */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Toolbar — hidden for search (search has its own) */}
        {!isSearch && (
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
            <h1 className="text-lg font-semibold text-foreground">
              {ENTITIES.find((e) => e.key === activeEntity)?.label}
            </h1>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showDeleted}
                  onChange={(e) => setShowDeleted(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                Show Deleted
              </label>
              <SearchBar
                value={search}
                onChange={setSearch}
                placeholder="Search..."
                className="w-52"
              />
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {isSearch && (
            <SearchPanel
              onNavigateToEntity={(entityKey) => {
                setActiveEntity(entityKey);
                setSearch('');
              }}
            />
          )}
          {activeEntity === 'customers' && <CustomerList search={search} showDeleted={showDeleted} />}
          {activeEntity === 'contacts' && <ContactList search={search} showDeleted={showDeleted} />}
          {activeEntity === 'vendors' && <VendorList search={search} showDeleted={showDeleted} />}
          {activeEntity === 'parts' && <PartList search={search} showDeleted={showDeleted} />}
          {activeEntity === 'parent-items' && <ParentItemList search={search} showDeleted={showDeleted} />}
          {activeEntity === 'bom-versions' && <BomVersionList search={search} showDeleted={showDeleted} />}
          {activeEntity === 'users' && <UserList search={search} showDeleted={showDeleted} />}
        </div>
      </div>
    </div>
  );
}
